<template>
    <div
        class='selectProductTable'
        :class="{
            'selectProductTable--sticky-header': stickyHeader
        }"
    >
        <div class="select-product-cards">
            <div
                v-if="tableLoc === 'select' && tableData.length > 0 && hasSelectableDevice"
                class="select-product-header"
            >
                <div
                    class="select-all-row"
                    @click="toggleSelectAll"
                >
                    <reo-checkbox
                        class="select-product-checkbox"
                        :modelValue="isAllSelectableSelected"
                        :indeterminate="isPartiallySelected"
                        :isRound="false"
                        @click.stop
                        @change="toggleSelectAll"
                    />
                    <span>{{ $t('selectAll') }}</span>
                </div>
            </div>
            <div
                v-if="tableData.length > 0"
                class="device-card-list"
            >
                <div
                    v-for="row of tableData"
                    :key="row.uid"
                    class="device-card"
                    @click="toggleCardRow(row)"
                >
                    <reo-checkbox
                        v-if="tableLoc === 'select'"
                        class="select-product-checkbox"
                        :modelValue="isRowSelected(row)"
                        :disabled="!selectable(row)"
                        :isRound="false"
                        @click.stop
                        @change="(val) => handleRowSelectChange(row, val)"
                    />
                    <span class="device-info">
                        <span class="device-name">{{ row.title }}</span>
                        <span class="device-uid">{{ row.uid }}</span>
                        <span
                            class="device-plan"
                            :class="getDevicePlanClass(row)"
                        >{{ getDevicePlanDisplayText(row) }}</span>
                    </span>
                </div>
            </div>
            <div v-else class="empty-device-list">
                <img
                    class="empty-device-image"
                    :src="NO_DEVICE_IMAGE"
                    alt=""
                />
                <p class="empty-device-text">{{ $t("devices.noDevice") }}</p>
                <reo-common-button
                    button-class="empty-device-action"
                    :label="$t('dialog.goApp').toString()"
                    :href="downloadAppUrl"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import ReoCheckbox from '@/components/reoCheckbox.vue';
import ReoCommonButton from '@/components/ReoCommonButton.vue';
import { uidHandler } from '@/lib';
import { EAssociateFailedReason, IDeviceAssociationResultItem, ITotalDevice } from '@reolink-cloud/interface';
import { config } from 'CONFIG';
import NO_DEVICE_IMAGE from '@/common/images/reolink/other/no_device.png';

interface ISelectProductDevice extends ITotalDevice {
    subName?: string;
}

type TSelectedDevice = Pick<ISelectProductDevice, 'uid' | 'modelTitle' | 'subscription' | 'status'> & {
    associatedAt?: number;
};

export default Vue.extend({

    components: {
        ReoCheckbox,
        ReoCommonButton
    },
    props: {
        tableLoc: String,
        selectedDevices: {
            default () {

                return [];

            },
            type: Array as PropType<TSelectedDevice[]>
        },
        selectLimit: Number,
        deviceData: {
            default (): ISelectProductDevice[] {

                return [];

            },
            type: Array as PropType<ISelectProductDevice[]>
        },
        availableModel: {
            default (): string[] {

                return [];

            },
            type: Array as PropType<string[]>
        },
        subId: String,
        deviceAssociateResults: {
            default (): Record<string, IDeviceAssociationResultItem> {

                return {};

            },
            type: Object as PropType<Record<string, IDeviceAssociationResultItem>>
        },
        stickyHeader: {
            type: Boolean,
            default: false
        }
    },
    data () {

        const inDoorbellProgress = this.$route.query.inDoorbellProgress;
        const disableUid = typeof inDoorbellProgress === 'string' ? uidHandler.decrypt( inDoorbellProgress ) ?? this.selectedDevices[0]?.uid : '';

        return {
            tableData: [] as ISelectProductDevice[],
            selection: [] as TSelectedDevice[],
            userSelectedDevices: this.selectedDevices,
            selectLimitation: this.selectLimit,
            disableUid,
            NO_DEVICE_IMAGE
        };

    },
    created () {

        let tableDataSelected: ISelectProductDevice[] = [];
        let tableDataUnassociated: ISelectProductDevice[] = [];
        let tableDataCanAssociated: ISelectProductDevice[] = [];
        // 将可选的设备提前
        for ( const item of this.deviceData ) {

            const normalizedItem = {
                ...item,
                title: item.title || item.modelTitle || item.uid || '—'
            };

            //过滤不支持该套餐的设备
            if ( !this.availableModel.includes( normalizedItem.modelTitle )) {

                continue;

            }

            if ( normalizedItem.status === 'associated' && normalizedItem.subscription === this.subId ) {

                tableDataSelected.push( normalizedItem );

            } else if ( normalizedItem.status === 'associated' && normalizedItem.subscription !== this.subId ) {

                tableDataCanAssociated.push( normalizedItem );

            } else if ( normalizedItem.status === 'unassociated' || normalizedItem.subscription === '' ) {

                tableDataUnassociated.push( normalizedItem );

            }

        }
        tableDataSelected.sort(( a, b ) => a.uid.localeCompare( b.uid ));
        tableDataUnassociated.sort(( a, b ) => a.uid.localeCompare( b.uid ));
        tableDataCanAssociated.sort(( a, b ) => a.uid.localeCompare( b.uid ));
        this.tableData = tableDataSelected.concat( tableDataUnassociated ).concat( tableDataCanAssociated );
        this.emitEmptyState();
        this.handleDefaultSelect();

    },

    watch: {
        selectLimit: {
            handler ( newVal: number ) {

                this.selectLimitation = newVal;

            }
        },
        selectedDevices: {
            deep: true,
            handler ( newVal: TSelectedDevice[] ) {

                this.userSelectedDevices = newVal;
                this.syncSelectionFromSelectedDevices();

            }
        }
    },
    methods: {
        emitEmptyState (): void {

            this.$emit( 'emptyStateChange', this.tableData.length === 0 );

        },
        getPlanText ( row: ISelectProductDevice ): string {

            if ( row.status !== 'unassociated' && row.planTitle ) {

                const planTitle = row.planTitle || '';
                const subName = row.subName || '';
                return `${planTitle}${subName && subName !== planTitle ? `(${subName})` : ''}`;

            }

            return this.$t( 'selectProduct.noPlanAssigned' ) as string;

        },
        getDeviceResult ( row: ISelectProductDevice ): IDeviceAssociationResultItem | undefined {

            return this.deviceAssociateResults[row.uid];

        },
        getDevicePlanDisplayText ( row: ISelectProductDevice ): string {

            const result = this.getDeviceResult( row );
            if ( !result ) {

                return this.getPlanText( row );

            }

            if ( result.status === 'success' ) {

                return result.type === 'associate'
                    ? this.$t( 'selectProduct.device_associate_success' ) as string
                    : this.$t( 'selectProduct.device_unassociate_success' ) as string;

            }

            if ( result.reason === EAssociateFailedReason.BIND_ERROR ) {

                return this.$t( 'selectProduct.device_bind_error' ) as string;

            }

            return this.$t( 'selectProduct.device_other_error' ) as string;

        },
        getDevicePlanClass ( row: ISelectProductDevice ): Record<string, boolean> {

            const result = this.getDeviceResult( row );
            return {
                'device-plan--success': result?.status === 'success',
                'device-plan--failed': result?.status === 'failed'
            };

        },
        isRowSelected ( row: ISelectProductDevice ): boolean {

            return this.selection.some( item => item.uid === row.uid );

        },
        getSelectedPayload ( row: ISelectProductDevice ): TSelectedDevice {

            return {
                uid: row.uid,
                modelTitle: row.modelTitle,
                subscription: row.subscription,
                status: row.status
            };

        },
        emitSelection (): void {

            this.$emit( 'selectedDevicesChange', this.selection );

        },
        setCardRowSelected ( row: ISelectProductDevice, selected: boolean ): void {

            if ( selected ) {

                if ( this.isRowSelected( row ) || !this.selectable( row )) {

                    return;

                }
                this.selection.push( this.getSelectedPayload( row ));

            } else {

                this.selection = this.selection.filter( item => item.uid !== row.uid );

            }

        },
        toggleCardRow ( row: ISelectProductDevice ): void {

            if ( this.tableLoc !== 'select' ) {

                return;

            }
            if ( !this.selectable( row )) {

                return;

            }

            this.setCardRowSelected( row, !this.isRowSelected( row ));
            this.emitSelection();

        },
        // 但达到选择上限，存在禁用的设备，也展示半选状态（产品要求）
        toggleSelectAll (): void {

            const selected = !( this.isAllSelectableSelected || this.isCurrentSelectableSelectionComplete );

            if ( selected ) {

                for ( let item of this.selectableRows ) {

                    if ( this.selection.length >= this.selectLimitation ) {

                        break;

                    }
                    this.setCardRowSelected( item, true );

                }

            } else {

                for ( let item of this.tableData ) {

                    if ( this.isRowSelected( item )) {

                        this.setCardRowSelected( item, false );

                    }

                }

            }
            this.emitSelection();

        },
        handleRowSelectChange ( row: ISelectProductDevice, selected: boolean ): void {

            if ( this.tableLoc !== 'select' ) {

                return;

            }
            if ( selected && !this.selectable( row )) {

                return;

            }
            this.setCardRowSelected( row, selected );
            this.emitSelection();

        },
        canSelectInPlan ( row: ISelectProductDevice ): boolean {

            return this.availableModel.includes( row.modelTitle ) && row.plan !== '0';

        },
        selectable ( row: ISelectProductDevice ): boolean {

            let index = this.selection.findIndex( v => v.uid === row.uid );
            if ( this.selection.length >= this.selectLimitation ) {

                if ( index !== -1 ) {

                    return true;

                } else {

                    return false;

                }

            } else {

                return this.canSelectInPlan( row );

            }

        },
        syncSelectionFromSelectedDevices (): void {

            if ( this.tableLoc !== 'select' ) {

                return;

            }

            const selectedUidSet = new Set( this.userSelectedDevices.map(( item ) => item.uid ));
            this.selection = this.tableData
                .filter( item => selectedUidSet.has( item.uid ))
                .map( item => this.getSelectedPayload( item ));

        },
        handleDefaultSelect (): void {

            if ( this.tableLoc !== 'select' ) {

                return;

            }
            this.userSelectedDevices.sort(( a, b ) => ( a.associatedAt ?? 0 ) - ( b.associatedAt ?? 0 ));
            this.$nextTick(() => {

                if ( this.disableUid !== '' ) {

                    const disableRow = this.tableData.find( item => item.uid === this.disableUid );
                    if ( disableRow ) {

                        this.setCardRowSelected( disableRow, true );

                    }
                    this.$emit( 'selectedDevicesChange', this.selection );

                }
                for ( let row of this.userSelectedDevices ) {

                    for ( let item of this.tableData ) {

                        if ( this.selection.length >= this.selectLimitation ) {

                            this.$emit( 'selectedDevicesChange', this.selection );
                            return;

                        }
                        if ( row.uid === item.uid && this.availableModel.includes( row.modelTitle )) {

                            this.setCardRowSelected( item, true );

                        }

                    }

                }
                this.emitSelection();

            });

        }
    },
    computed: {
        selectableRows(): ISelectProductDevice[] {

            return this.tableData.filter( row => this.selectable( row ));

        },
        hasSelectableDevice(): boolean {

            return this.selectableRows.length > 0;

        },
        isAllSelectableSelected(): boolean {

            return this.tableData.length > 0 && this.tableData.every( row => this.isRowSelected( row ));

        },
        isPartiallySelected(): boolean {

            return this.tableData.length > 0
                && !this.isAllSelectableSelected
                && this.tableData.some( row => this.isRowSelected( row ));

        },
        isCurrentSelectableSelectionComplete(): boolean {

            return this.selectableRows.length > 0
                && this.selectableRows.every( row => this.isRowSelected( row ));

        },
        downloadAppUrl(): string {

            return `${config.reolink}/software-and-manual/`;

        }

    }
});
</script>

<style lang="less" scoped>

    .selectProductTable {
        
        .select-product-cards {
            display: flex;
            flex-direction: column;
        }

        .select-product-header {
            margin-bottom: 12px;
            background-color: @bg_01;

            @media @mobile {
                margin-bottom: 3px;
            }
        }

        &.selectProductTable--sticky-header {
            .select-product-header {
                position: sticky;
                top: 0;
                z-index: 20;
                padding-bottom: 12px;
                margin-bottom: 0;

                @media @mobile {
                    padding-bottom: 3px;
                }
            }
        }

        .select-all-row {
            display: inline-flex;
            color: @b_6;
            font-size: 16px;
            line-height: 1.4;
            font-family: @font_02;
            font-weight: 500;
            cursor: pointer;
        }

        .select-product-checkbox {
            margin-right: 12px;
        }

        .select-product-header .select-product-checkbox {
            /deep/ .check-icon svg{
                display: block;
                position: absolute;
                top: 0;
                left: 0;
            }

            /deep/ .check-icon.icon-indeterminate svg{
                display: none;
            }
        }

        .device-card-list {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 16px;
            padding-top: 16px;

            @media @mobile {
                grid-template-columns: 1fr;
                gap: 12px;
            }
        }

        .device-card {
            display: flex;
            align-items: center;
            padding: 12px 16px;
            border-radius: 12px;
            background-color: @bg_02;
            color: @font_primary;
            cursor: pointer;

            @media @mobile {
                border-radius: 16px;
            }
        }

        .device-info {
            display: flex;
            flex-direction: column;
            gap: 4px;
        }

        .device-name {
            color: @font_primary;
            font-size: 16px;
            font-family: @font_02;
            font-weight: 500;
        }

        .device-uid,
        .device-plan {
            color: @font_secondary;
            font-size: 14px;
            font-family: @font_02;
        }

        .device-plan--success {
            color: #3BCB62;
        }

        .device-plan--failed {
            color: #FF4747;
        }

        .empty-device-list {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 20px;
            min-height: 320px;
            color: @font_secondary;
            font-family: @font_02;
            font-size: 14px;
            font-weight: 400;
            line-height: 20px;
            text-align: center;

            @media @mobile {
                gap: 12px;
                min-height: 260px;
                font-size: 12px;
                line-height: 18px;
            }
        }

        .empty-device-image {
            width: 120px;
            height: 120px;
            object-fit: contain;

            @media @mobile {
                width: 72px;
                height: 72px;
            }
        }

        .empty-device-text {
            color: @font_secondary;
            margin: 0;
        }

        /deep/ .reo-button-inner.empty-device-action {
            padding: 10px 20px !important;
            font-size: 14px;
            line-height: 20px;
            font-weight: 500;

            @media @mobile {
                padding: 8px 20px !important;
            }
        }
    }

</style>
